import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Check, Ban, CircleSlash, Loader2, ImageOff,
  ShieldCheck, AlertTriangle, Wrench, MapPin, Lock, Unlock, ZoomIn, PlusCircle, Undo2
} from 'lucide-react';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';
import { VisualizadorFoto, FotoAmpliavel } from './VisualizadorFoto';
import { PreviewAr } from './PreviewAr';
import {
  CotacaoMaquina, CotacaoParte, StatusParte, ROTULO_STATUS_PARTE, ROTULO_SECAO,
  corClassificacao, moeda, parseValor,
} from './cotacao-types';

export interface DadosParte {
  valor?: number | null;
  status?: StatusParte;
  observacoes?: string | null;
  adequada?: boolean;
}

interface Props {
  maquina: CotacaoMaquina;
  /** Persiste e devolve a parte atualizada; a tela aplica otimisticamente. */
  onSalvarParte: (parteId: string, dados: DadosParte) => Promise<boolean>;
  onSalvarMaquina: (dados: { modoValor?: 'parte' | 'unico'; valorUnico?: number | null }) => Promise<boolean>;
}

/**
 * Ferramenta de precificação: foto da máquina + foto da parte + solução da AR +
 * campo de valor, uma parte por vez.
 *
 * Só as partes NÃO adequadas entram na fila — as adequadas ficam num painel
 * separado, como evidência de que foram avaliadas.
 */
export function PainelPrecificacao({ maquina, onSalvarParte, onSalvarMaquina }: Props) {
  const naoAdequadas = useMemo(() => maquina.partes.filter((p) => !p.adequada), [maquina.partes]);
  const adequadas = useMemo(() => maquina.partes.filter((p) => p.adequada), [maquina.partes]);

  const [indice, setIndice] = useState(0);
  const [mostrarAdequadas, setMostrarAdequadas] = useState(false);
  const [fotoMaquinaAtiva, setFotoMaquinaAtiva] = useState(0);
  const [ampliada, setAmpliada] = useState<{ fotos: FotoAmpliavel[]; indice: number } | null>(null);
  const [focarParteId, setFocarParteId] = useState<string | null>(null);

  // Cada salvamento recria `maquina.partes`, e portanto `naoAdequadas`. Ler a
  // lista por ref mantém o efeito abaixo preso à TROCA DE MÁQUINA: com ela na
  // lista de dependências, todo salvamento reposicionava a fila e "Salvar e
  // seguir" voltava para a primeira pendente em vez de ir para a próxima.
  const naoAdequadasRef = useRef(naoAdequadas);
  naoAdequadasRef.current = naoAdequadas;

  // Trocar de máquina recomeça a fila na primeira parte pendente: é onde o
  // trabalho parou, e não no começo de novo.
  useEffect(() => {
    const primeiraPendente = naoAdequadasRef.current.findIndex((p) => p.status === 'pendente');
    setIndice(primeiraPendente === -1 ? 0 : primeiraPendente);
    setFotoMaquinaAtiva(0);
  }, [maquina.id]);

  // Uma parte trazida do painel de adequadas entra na fila pela ordem do
  // documento, não no fim — por isso o foco é buscado pelo id.
  useEffect(() => {
    if (!focarParteId) return;
    const alvo = naoAdequadas.findIndex((p) => p.id === focarParteId);
    if (alvo !== -1) {
      setIndice(alvo);
      setFocarParteId(null);
    }
  }, [focarParteId, naoAdequadas]);

  // Tirar uma parte do orçamento encurta a fila; o índice não pode ficar solto.
  useEffect(() => {
    setIndice((i) => Math.min(i, Math.max(0, naoAdequadas.length - 1)));
  }, [naoAdequadas.length]);

  const parte = naoAdequadas[indice] ?? null;
  const modoUnico = maquina.modoValor === 'unico';

  const irPara = useCallback(
    (novo: number) => setIndice(Math.max(0, Math.min(naoAdequadas.length - 1, novo))),
    [naoAdequadas.length]
  );

  /** Traz uma parte adequada para a fila de orçamento (e vice-versa). */
  const alternarInclusao = useCallback(
    async (alvo: CotacaoParte) => {
      const incluindo = alvo.adequada;
      const ok = await onSalvarParte(alvo.id, {
        adequada: !incluindo,
        // Entra ou sai sempre zerada: um preço antigo não pode voltar sozinho.
        valor: null,
        status: 'pendente' as StatusParte,
      });
      if (ok && incluindo) {
        setMostrarAdequadas(false);
        setFocarParteId(alvo.id);
      }
    },
    [onSalvarParte]
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
      <div className="min-w-0">
        <CabecalhoMaquina
          maquina={maquina}
          modoUnico={modoUnico}
          onSalvarMaquina={onSalvarMaquina}
        />

        {modoUnico ? (
          <ValorFechado maquina={maquina} onSalvarMaquina={onSalvarMaquina} />
        ) : naoAdequadas.length === 0 ? (
          <div className="mt-4 text-center py-14 bg-white dark:bg-schmersal-gray-darker rounded-xl">
            <ShieldCheck className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold text-schmersal-blue dark:text-white">
              Nenhuma parte não adequada nesta máquina
            </p>
            <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1">
              A AR não apontou soluções para nenhum ponto. Se ainda assim houver escopo a orçar,
              inclua uma parte adequada pelo painel ao lado.
            </p>
          </div>
        ) : parte ? (
          <CartaoParte
            key={parte.id}
            parte={parte}
            posicao={indice + 1}
            total={naoAdequadas.length}
            onAnterior={() => irPara(indice - 1)}
            onProxima={() => irPara(indice + 1)}
            onAmpliarFoto={() =>
              parte.fotoUrl &&
              setAmpliada({
                fotos: [{ url: parte.fotoUrl, urlHd: parte.fotoHdUrl, legenda: parte.titulo }],
                indice: 0,
              })
            }
            onRemoverDoOrcamento={() => alternarInclusao(parte)}
            onSalvar={async (dados) => {
              const ok = await onSalvarParte(parte.id, dados);
              // Avançar sozinho só faz sentido quando a parte foi resolvida.
              if (ok && indice < naoAdequadas.length - 1) irPara(indice + 1);
              return ok;
            }}
            onSalvarSemAvancar={(dados) => onSalvarParte(parte.id, dados)}
          />
        ) : null}

        <PreviewAr
          pdfUrl={maquina.pdfUrl}
          pagina={modoUnico ? null : parte?.paginaOrigem ?? null}
          nomeMaquina={maquina.nome}
          contexto={modoUnico ? maquina.nome : parte?.titulo ?? maquina.nome}
        />
      </div>

      <div className="space-y-4">
        <FotosDaMaquina
          maquina={maquina}
          ativa={fotoMaquinaAtiva}
          onSelecionar={setFotoMaquinaAtiva}
          onAmpliar={(i) =>
            setAmpliada({
              fotos: maquina.fotos.map((f) => ({ url: f.url, urlHd: f.urlHd, legenda: f.legenda })),
              indice: i,
            })
          }
        />

        {!modoUnico && naoAdequadas.length > 0 && (
          <NavegadorPartes partes={naoAdequadas} indice={indice} onSelecionar={irPara} />
        )}

        <PainelAdequadas
          partes={adequadas}
          aberto={mostrarAdequadas}
          onAlternar={() => setMostrarAdequadas((v) => !v)}
          onIncluir={alternarInclusao}
          onAmpliar={(p) =>
            p.fotoUrl &&
            setAmpliada({ fotos: [{ url: p.fotoUrl, urlHd: p.fotoHdUrl, legenda: p.titulo }], indice: 0 })
          }
        />
      </div>

      {ampliada && (
        <VisualizadorFoto
          fotos={ampliada.fotos}
          indiceInicial={ampliada.indice}
          onFechar={() => setAmpliada(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CabecalhoMaquina({
  maquina, modoUnico, onSalvarMaquina,
}: {
  maquina: CotacaoMaquina;
  modoUnico: boolean;
  onSalvarMaquina: Props['onSalvarMaquina'];
}) {
  const [alternando, setAlternando] = useState(false);

  const alternarModo = async () => {
    setAlternando(true);
    await onSalvarMaquina({ modoValor: modoUnico ? 'parte' : 'unico' });
    setAlternando(false);
  };

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-schmersal-blue dark:text-white">{maquina.nome}</h2>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light">
            {maquina.tag && <span className="font-mono">TAG {maquina.tag}</span>}
            {maquina.setor && <span>{maquina.setor}</span>}
            {maquina.tipo && <span>{maquina.tipo}</span>}
            {maquina.numeroAr && <span className="font-mono">{maquina.numeroAr}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold">
              {maquina.resumo.adequadas} adequada(s)
            </span>
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">
              {maquina.resumo.naoAdequadas} não adequada(s)
            </span>
            {!modoUnico && maquina.resumo.pendentes > 0 && (
              <span className="px-2 py-0.5 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light font-semibold">
                {maquina.resumo.pendentes} sem preço
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-schmersal-gray-medium">Total da máquina</div>
          <div className="text-2xl font-bold text-schmersal-blue dark:text-schmersal-turquoise">
            {moeda(maquina.resumo.total)}
          </div>
          <button
            onClick={alternarModo}
            disabled={alternando}
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:text-schmersal-blue dark:hover:text-schmersal-turquoise disabled:opacity-50"
          >
            {alternando ? <Loader2 className="h-3 w-3 animate-spin" /> : modoUnico ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            {modoUnico ? 'Voltar a cotar por parte' : 'Usar valor fechado'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ValorFechado({
  maquina, onSalvarMaquina,
}: {
  maquina: CotacaoMaquina;
  onSalvarMaquina: Props['onSalvarMaquina'];
}) {
  const [texto, setTexto] = useState(maquina.valorUnico !== null ? String(maquina.valorUnico).replace('.', ',') : '');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setTexto(maquina.valorUnico !== null ? String(maquina.valorUnico).replace('.', ',') : '');
  }, [maquina.id, maquina.valorUnico]);

  const salvar = async () => {
    setSalvando(true);
    await onSalvarMaquina({ valorUnico: texto.trim() ? parseValor(texto) : null });
    setSalvando(false);
  };

  return (
    <div className="mt-4 bg-white dark:bg-schmersal-gray-darker rounded-xl p-5">
      <h3 className="font-bold text-schmersal-blue dark:text-white flex items-center gap-2">
        <Lock className="h-4 w-4" /> Valor fechado da máquina
      </h3>
      <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-1">
        Um único preço cobre a adequação das {maquina.resumo.naoAdequadas} partes não adequadas.
        As partes continuam listadas na proposta como escopo, sem valor individual.
      </p>
      <div className="flex items-end gap-3 mt-4">
        <label className="flex-1 max-w-xs">
          <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">
            Valor total (R$)
          </span>
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={salvar}
            onKeyDown={(e) => e.key === 'Enter' && salvar()}
            inputMode="decimal"
            placeholder="0,00"
            className="w-full px-3 py-2.5 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-lg font-bold text-schmersal-blue dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
          />
        </label>
        {salvando && <Loader2 className="h-4 w-4 animate-spin text-schmersal-gray-medium mb-3" />}
      </div>
    </div>
  );
}

function CartaoParte({
  parte, posicao, total, onAnterior, onProxima, onSalvar, onSalvarSemAvancar,
  onAmpliarFoto, onRemoverDoOrcamento,
}: {
  parte: CotacaoParte;
  posicao: number;
  total: number;
  onAnterior: () => void;
  onProxima: () => void;
  onSalvar: (dados: DadosParte) => Promise<boolean>;
  onSalvarSemAvancar: (dados: DadosParte) => Promise<boolean>;
  onAmpliarFoto: () => void;
  onRemoverDoOrcamento: () => void;
}) {
  const [texto, setTexto] = useState(parte.valor !== null ? String(parte.valor).replace('.', ',') : '');
  const [observacoes, setObservacoes] = useState(parte.observacoes ?? '');
  const [salvando, setSalvando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [parte.id]);

  const valorInvalido = texto.trim().length > 0 && parseValor(texto) === null;

  const confirmarValor = async () => {
    if (valorInvalido) return;
    setSalvando(true);
    await onSalvar({ valor: texto.trim() ? parseValor(texto) : null });
    setSalvando(false);
  };

  const marcar = async (status: StatusParte) => {
    setSalvando(true);
    await onSalvar({ status, valor: status === 'pendente' ? null : 0 });
    setSalvando(false);
  };

  return (
    <div className="mt-4 bg-white dark:bg-schmersal-gray-darker rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-schmersal-blue/10 text-schmersal-blue dark:bg-schmersal-turquoise/20 dark:text-schmersal-turquoise whitespace-nowrap">
            {ROTULO_SECAO[parte.secao]}
          </span>
          <h3 className="font-bold text-schmersal-blue dark:text-white truncate">{parte.titulo}</h3>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onRemoverDoOrcamento}
            title="Marcar como adequada e tirar do orçamento"
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:text-emerald-600 dark:hover:text-emerald-400"
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Tirar do orçamento</span>
          </button>
          <span className="text-xs text-schmersal-gray-medium mr-1">{posicao} de {total}</span>
          <button onClick={onAnterior} disabled={posicao === 1} className="p-1.5 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={onProxima} disabled={posicao === total} className="p-1.5 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-5 p-4">
        <div>
          <Foto url={parte.fotoUrl} alt={parte.titulo} altura="h-64" onAmpliar={onAmpliarFoto} />
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {parte.hrn !== null && (
              <span className={`text-xs px-2 py-1 rounded font-semibold ${corClassificacao(parte.classificacao)}`}>
                HRN {parte.hrn} · {parte.classificacao}
              </span>
            )}
            {parte.categoria && (
              <span className="text-xs px-2 py-1 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light">
                {parte.categoria}
              </span>
            )}
          </div>
          {parte.localizacao && (
            <p className="flex items-center gap-1.5 text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light mt-2">
              <MapPin className="h-3 w-3" /> {parte.localizacao}
            </p>
          )}
        </div>

        <div className="min-w-0 space-y-4">
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-200/70 dark:border-amber-700/40 p-3">
            <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1.5">
              <Wrench className="h-3.5 w-3.5" /> Possíveis soluções (da AR)
            </h4>
            {parte.possiveisSolucoes ? (
              <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light whitespace-pre-line leading-relaxed">
                {parte.possiveisSolucoes}
              </p>
            ) : (
              // Acontece quando a parte foi trazida do painel de adequadas: a AR
              // não apontou solução, então o escopo vem da observação abaixo.
              <p className="text-sm italic text-schmersal-gray-medium leading-relaxed">
                A AR não apontou solução para este ponto — ele foi incluído no orçamento manualmente.
                Descreva o escopo no campo de observação abaixo; é ele que vai para a proposta.
              </p>
            )}
          </div>

          {(parte.perigos.length > 0 || parte.riscos.length > 0 || parte.sistemasSeguranca.length > 0) && (
            <div className="grid sm:grid-cols-3 gap-3">
              <ListaChips titulo="Perigos" itens={parte.perigos} icone={AlertTriangle} />
              <ListaChips titulo="Riscos" itens={parte.riscos} icone={AlertTriangle} />
              <ListaChips titulo="Segurança atual" itens={parte.sistemasSeguranca} icone={ShieldCheck} />
            </div>
          )}

          <div className="pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
            <div className="flex flex-wrap items-end gap-3">
              <label className="w-44">
                <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">
                  Valor da correção (R$)
                </span>
                <input
                  ref={inputRef}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && confirmarValor()}
                  inputMode="decimal"
                  placeholder="0,00"
                  className={`w-full px-3 py-2.5 rounded-lg border bg-white dark:bg-schmersal-gray-darkest text-lg font-bold text-schmersal-blue dark:text-white focus:outline-none focus:ring-2 ${
                    valorInvalido
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-schmersal-gray dark:border-schmersal-gray-dark focus:ring-schmersal-blue'
                  }`}
                />
              </label>

              <button
                onClick={confirmarValor}
                disabled={salvando || valorInvalido}
                className="flex items-center gap-2 px-4 py-2.5 bg-schmersal-blue hover:bg-schmersal-blue-light disabled:opacity-50 text-white rounded-lg font-semibold text-sm transition-colors"
              >
                {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Salvar e seguir
              </button>

              <button
                onClick={() => marcar('sem_custo')}
                disabled={salvando}
                title="Entra na proposta como escopo, sem valor"
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue disabled:opacity-50"
              >
                <CircleSlash className="h-3.5 w-3.5" /> Sem custo
              </button>

              <button
                onClick={() => marcar('nao_aplicavel')}
                disabled={salvando}
                title="Fica fora do orçamento"
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue disabled:opacity-50"
              >
                <Ban className="h-3.5 w-3.5" /> Não aplicável
              </button>

              <span className={`text-xs font-semibold px-2 py-1 rounded ${
                parte.status === 'pendente'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              }`}>
                {ROTULO_STATUS_PARTE[parte.status]}
              </span>
            </div>

            {valorInvalido && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">
                Valor não reconhecido. Use 12500 ou 12.500,00.
              </p>
            )}

            <label className="block mt-3">
              <span className="block text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light mb-1">
                Observação da proposta (opcional)
              </span>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                onBlur={() => {
                  if ((parte.observacoes ?? '') !== observacoes) {
                    onSalvarSemAvancar({ observacoes: observacoes.trim() || null });
                  }
                }}
                rows={2}
                placeholder="Ex.: inclui projeto elétrico e comissionamento"
                className="w-full px-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue resize-none"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListaChips({ titulo, itens, icone: Icone }: { titulo: string; itens: string[]; icone: typeof AlertTriangle }) {
  if (itens.length === 0) return null;
  return (
    <div>
      <h4 className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-schmersal-gray-medium mb-1.5">
        <Icone className="h-3 w-3" /> {titulo}
      </h4>
      <ul className="space-y-1">
        {itens.map((item, i) => (
          <li key={`${item}-${i}`} className="text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light leading-snug">
            · {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FotosDaMaquina({
  maquina, ativa, onSelecionar, onAmpliar,
}: {
  maquina: CotacaoMaquina;
  ativa: number;
  onSelecionar: (i: number) => void;
  onAmpliar: (i: number) => void;
}) {
  if (maquina.fotos.length === 0) {
    return (
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-4">
        <h3 className="text-xs font-bold uppercase tracking-wide text-schmersal-gray-medium mb-2">Equipamento</h3>
        <Foto url={null} alt={maquina.nome} altura="h-40" />
      </div>
    );
  }

  const indiceAtivo = Math.min(ativa, maquina.fotos.length - 1);
  const foto = maquina.fotos[indiceAtivo];

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-4">
      <h3 className="text-xs font-bold uppercase tracking-wide text-schmersal-gray-medium mb-2">
        Equipamento {foto.legenda ? `· ${foto.legenda}` : ''}
      </h3>
      <Foto
        url={foto.url}
        alt={foto.legenda ?? maquina.nome}
        altura="h-48"
        onAmpliar={() => onAmpliar(indiceAtivo)}
      />
      {maquina.fotos.length > 1 && (
        <div className="grid grid-cols-4 gap-1.5 mt-2">
          {maquina.fotos.map((f, i) => (
            <button
              key={f.id}
              onClick={() => onSelecionar(i)}
              title={f.legenda ?? undefined}
              className={`rounded overflow-hidden border-2 transition-colors ${
                i === ativa ? 'border-schmersal-turquoise' : 'border-transparent hover:border-schmersal-gray'
              }`}
            >
              <img src={toAbsoluteFotoUrl(f.url)} alt="" loading="lazy" className="w-full h-12 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NavegadorPartes({
  partes, indice, onSelecionar,
}: {
  partes: CotacaoParte[];
  indice: number;
  onSelecionar: (i: number) => void;
}) {
  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-4">
      <h3 className="text-xs font-bold uppercase tracking-wide text-schmersal-gray-medium mb-2">
        Partes a cotar ({partes.length})
      </h3>
      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {partes.map((p, i) => (
          <button
            key={p.id}
            onClick={() => onSelecionar(i)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs transition-colors ${
              i === indice
                ? 'bg-schmersal-blue text-white'
                : 'hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light'
            }`}
          >
            {p.fotoUrl ? (
              <img src={toAbsoluteFotoUrl(p.fotoUrl)} alt="" loading="lazy" className="w-8 h-8 rounded object-cover flex-shrink-0" />
            ) : (
              <span className="w-8 h-8 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex items-center justify-center flex-shrink-0">
                <ImageOff className="h-3 w-3 text-schmersal-gray-medium" />
              </span>
            )}
            <span className="flex-1 truncate">{p.titulo}</span>
            <span className={`font-semibold whitespace-nowrap ${i === indice ? 'text-white' : p.status === 'pendente' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {p.status === 'pendente' ? '—' : moeda(p.valorEfetivo)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Partes que a AR classificou como adequadas.
 *
 * Ficam listadas como evidência de que foram avaliadas, e cada uma pode ser
 * puxada para o orçamento: acontece de o cliente querer adequar um ponto que já
 * atende a norma (padronização, upgrade de categoria), e nesse caso o escopo
 * não vem da AR — o vendedor descreve na observação da parte.
 */
function PainelAdequadas({
  partes, aberto, onAlternar, onIncluir, onAmpliar,
}: {
  partes: CotacaoParte[];
  aberto: boolean;
  onAlternar: () => void;
  onIncluir: (parte: CotacaoParte) => void;
  onAmpliar: (parte: CotacaoParte) => void;
}) {
  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-xl p-4">
      <button onClick={onAlternar} className="w-full flex items-center justify-between text-left">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Partes adequadas ({partes.length})
        </h3>
        <ChevronRight className={`h-4 w-4 text-schmersal-gray-medium transition-transform ${aberto ? 'rotate-90' : ''}`} />
      </button>
      {aberto && (
        partes.length === 0 ? (
          <p className="text-xs text-schmersal-gray-medium mt-2">Nenhuma parte desta máquina ficou adequada.</p>
        ) : (
          <>
            <p className="text-[11px] text-schmersal-gray-medium mt-2">
              Não entram no preço. Use “incluir” se alguma delas fizer parte do escopo mesmo assim.
            </p>
            <ul className="mt-2 space-y-1 max-h-72 overflow-y-auto pr-1">
              {partes.map((p) => (
                <li
                  key={p.id}
                  className="group flex items-start gap-2 text-xs p-1 rounded-lg hover:bg-schmersal-gray-light/60 dark:hover:bg-schmersal-gray-dark/40"
                >
                  {p.fotoUrl ? (
                    <button
                      onClick={() => onAmpliar(p)}
                      title="Ampliar foto"
                      className="relative flex-shrink-0"
                    >
                      <img src={toAbsoluteFotoUrl(p.fotoUrl)} alt="" loading="lazy" className="w-9 h-9 rounded object-cover" />
                      <span className="absolute inset-0 rounded bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <ZoomIn className="h-3.5 w-3.5 text-white" />
                      </span>
                    </button>
                  ) : (
                    <span className="w-9 h-9 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light truncate">{p.titulo}</p>
                    <p className="text-schmersal-gray-medium truncate">
                      {p.sistemasSeguranca.join(' · ') || 'Sem solução requerida na AR'}
                    </p>
                  </div>
                  <button
                    onClick={() => onIncluir(p)}
                    title="Incluir esta parte no orçamento"
                    className="flex items-center gap-1 px-1.5 py-1 rounded text-[11px] font-semibold text-schmersal-gray-medium hover:text-schmersal-blue dark:hover:text-schmersal-turquoise flex-shrink-0"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    <span className="hidden xl:inline">incluir</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )
      )}
    </div>
  );
}

function Foto({
  url, alt, altura, onAmpliar,
}: {
  url: string | null;
  alt: string;
  altura: string;
  onAmpliar?: () => void;
}) {
  const [falhou, setFalhou] = useState(false);

  useEffect(() => setFalhou(false), [url]);

  if (!url || falhou) {
    return (
      <div className={`${altura} w-full rounded-lg bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex flex-col items-center justify-center text-schmersal-gray-medium`}>
        <ImageOff className="h-6 w-6 mb-1" />
        <span className="text-xs">Sem foto no documento</span>
      </div>
    );
  }

  const imagem = (
    <img
      src={toAbsoluteFotoUrl(url)}
      alt={alt}
      loading="lazy"
      onError={() => setFalhou(true)}
      className={`${altura} w-full rounded-lg object-cover bg-schmersal-gray-light dark:bg-schmersal-gray-dark`}
    />
  );

  if (!onAmpliar) return imagem;

  return (
    <button onClick={onAmpliar} title="Clique para ampliar" className="group relative block w-full">
      {imagem}
      <span className="absolute inset-0 rounded-lg bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/60 text-white text-xs font-semibold">
          <ZoomIn className="h-4 w-4" /> Ampliar
        </span>
      </span>
    </button>
  );
}
